import { agentHubPaths } from '../../util/constants';
import type { StoreReadiness } from '../../store/modules/merchant/types';

const hasCatalogSyncDate = (lastCatalogSyncAt: unknown): boolean => {
  if (lastCatalogSyncAt == null || lastCatalogSyncAt === '') {
    return false;
  }
  if (typeof lastCatalogSyncAt === 'string' && lastCatalogSyncAt.trim().length > 0) {
    return true;
  }
  return false;
};

export type StoreReadinessOptions = {
  /** Whether the platform has a provisioning checklist that must be complete to go live. */
  hasProvisioning?: boolean;
};

/**
 * Whether an agent can go live from a store-readiness perspective.
 *
 * - All merchants: require lastCatalogSyncAt.
 * - Merchants with a provisioning checklist: also require storeReadiness.status === "complete".
 * - Other merchants: pending provisioning does not block going live once the catalog has synced.
 *
 * Missing storeReadiness is treated as ready for legacy documents.
 */
export const isAgenticStoreReady = (
  storeReadiness: StoreReadiness | null | undefined,
  { hasProvisioning = true }: StoreReadinessOptions = {},
): boolean => {
  if (storeReadiness == null) {
    return true;
  }

  const provisioningComplete = !hasProvisioning || storeReadiness.status === 'complete';

  return provisioningComplete && hasCatalogSyncDate(storeReadiness.lastCatalogSyncAt);
};

export const needsCatalogSync = (storeReadiness: StoreReadiness | null | undefined): boolean => {
  if (storeReadiness == null) {
    return false;
  }

  return !hasCatalogSyncDate(storeReadiness.lastCatalogSyncAt);
};

export type StoreReadinessLinkTarget =
  typeof agentHubPaths.PROVISIONING | typeof agentHubPaths.CATALOG;

export type StoreReadinessPendingItem = {
  id: 'provisioning' | 'catalog';
  label: string;
  path: StoreReadinessLinkTarget;
};

export const getPendingStoreReadinessItems = (
  storeReadiness: StoreReadiness | null | undefined,
  { hasProvisioning = true }: StoreReadinessOptions = {},
): StoreReadinessPendingItem[] => {
  if (storeReadiness == null) {
    return [];
  }

  const items: StoreReadinessPendingItem[] = [];

  if (hasProvisioning && storeReadiness.status !== 'complete') {
    items.push({
      id: 'provisioning',
      label: 'Complete store provisioning',
      // The index redirects to the merchant's own platform checklist.
      path: agentHubPaths.PROVISIONING,
    });
  }

  if (!hasCatalogSyncDate(storeReadiness.lastCatalogSyncAt)) {
    items.push({
      id: 'catalog',
      label: 'Complete catalog sync',
      path: agentHubPaths.CATALOG,
    });
  }

  return items;
};
